<script>
// @ts-check

import BaseErrors from '@/components/base/BaseErrors.vue'
import BaseLabel from '@/components/base/BaseLabel.vue'
import { attrsMixin } from '@/core/mixins/attrs'
import { id } from '@/core/props'
import * as _ from 'lodash-es'
import { nextTick } from 'vue'

/**
 * Deprecated select component
 * @deprecated Use VSelect instead
 */

export default {
  name: 'BaseSelect',
  components: {
    BaseLabel,
    BaseErrors
  },
  mixins: [attrsMixin],
  props: {
    id,
    /** @type {import('vue').Prop<any>} */
    modelValue: {
      default: undefined,
      validator: (value) =>
        ['string', 'number', 'boolean'].includes(typeof value) || value === null
    },
    label: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    errors: {
      /** @type {import('vue').PropType<string[]|null>} */
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean
    },
    valuePath: {
      type: String,
      default: 'value'
    },
    labelPath: {
      type: String,
      default: 'label'
    },
    required: {
      type: Boolean
    },
    info: {
      type: String,
      default: null
    },
    isOcrValue: {
      type: Boolean
    }
  },
  emits: ['update:modelValue'],
  computed: {
    hasErrors() {
      return this.errors.length > 0
    },
    translatedOptions() {
      return this.options.map((option) => {
        /*
        If the options are inside objects, you'll need to define a path to
        the 'label' and 'value' values, unless they are inside 'value' and
        'label' properties of that object (default path). For example, the
        values could be inside the objects data property, so the path would be
        'data.id', and label path 'data.name'.

        An addition to the labels has been made:
        You can set multiple values as a label by giving the paths separated by a comma.
        This is useful if the labels are for example 'data.first_name' and 'data.last_name'.
        You then just pass the labels like 'data.first_name,data.last_name'
        */
        if (typeof option === 'object') {
          const label = this.labelPath.includes(',')
            ? this.joinLabels(option, this.labelPath)
            : _.get(option, this.labelPath).toString()

          const variables = option.variables || []
          return {
            value: _.get(option, this.valuePath),
            label: option.translate ? this.$t(label, variables) : label
          }
          /*
          If the array of options has just string, the string value will be given to both 'value' and 'label. */
        }
        if (typeof option === 'string' || typeof option === 'number') {
          return {
            value: option,
            label: option
          }
        }

        return {}
      })
    }
  },
  watch: {
    translatedOptions: {
      deep: true,
      handler() {
        nextTick().then(() => {
          // Re-render component in case of async option fetching to compact
          // strange browser behaviour of prefilling the first option as a placeholder
          this.$forceUpdate()
        })
      }
    }
  },
  methods: {
    joinLabels(option, labels) {
      return labels
        .split(',')
        .map((label) => _.get(option, label))
        .join(' ')
    }
  }
}
</script>

<template>
  <div class="w-auto" :class="classes" :style="styles">
    <BaseLabel :for="id" :info :required>
      {{ label }}
    </BaseLabel>
    <div class="spinner relative fill-current" :class="{ loading }">
      <select
        v-bind="attributes"
        :id
        :value="modelValue"
        class="text-input truncate pr-6"
        :class="{
          'border border-green bg-green-50': isOcrValue,
          'has-error': hasErrors,
          'pr-20': loading
        }"
        :disabled="loading || attributes.disabled"
        @change="$emit('update:modelValue', $event.target.value)"
      >
        <template v-if="$slots.placeholder">
          <option value="">
            <slot name="placeholder" />
          </option>
        </template>
        <option
          v-for="option in translatedOptions"
          :key="`${id}-option-${option.value}`"
          :value="option.value"
          :selected="option.value === modelValue"
        >
          {{ option.label }}
        </option>
      </select>
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex rounded-r px-1"
      >
        <VIcon icon="$chevron-down" size="small" class="self-center" />
      </div>
    </div>

    <BaseErrors class="mt-2" :label :errors />

    <slot />
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

select:disabled {
  @apply bg-secondary-200;
}

.spinner {
  @apply block;

  &.loading::after {
    display: block;
    content: '';
    height: 1rem;
    width: 1rem;
    position: absolute;
    top: calc(50% - (1rem / 2));
    right: 3.25rem;
    border: 2px solid var(--color-primary);
    border-radius: 9999px;
    border-color: transparent transparent var(--color-primary)
      var(--color-primary);
    animation: donut-spin 0.9s infinite linear;
  }
}
</style>
